/**
 * Adaptateur entre l'éditeur V2 (un bloc = un `data.visuals`) et le composant
 * réutilisable `VisualBlocksEditor` qui fournit toute l'UX (extraction depuis
 * image, ajout manuel, édition JSON, réordonnance, suppression, édition
 * inline du titre/légende).
 *
 * Source de vérité : `block.data.visuals`. On le lit, on délègue à l'éditeur,
 * et on persiste via `onPatchData({ ...data, visuals: next })` (clé retirée
 * si vide pour ne pas laisser un tableau vide en base).
 */

import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import { VisualBlocksEditor } from '@/components/visual-blocks/visual-blocks-editor';
import type { ExerciseBlock } from './types';

const VISUALS_SUPPORTED: ReadonlyArray<ExerciseBlock['block_type']> = [
  'statement',
  'statement_question',
  'correction',
  'distractor',
  'explanation',
];

interface VisualsEditorPanelProps {
  block: ExerciseBlock;
  isDisabled: boolean;
  onPatchData: (data: Record<string, unknown>) => void;
  /** Détache un visuel pour le poser seul dans un nouveau bloc énoncé. */
  onIsolateVisual?: (visualId: string) => void;
}

export function VisualsEditorPanel({
  block,
  isDisabled,
  onPatchData,
  onIsolateVisual,
}: VisualsEditorPanelProps) {
  // Les types qui n'acceptent pas `visuals` côté schéma (hint, note) sont exclus.
  if (!VISUALS_SUPPORTED.includes(block.block_type)) return null;

  const raw = (block.data as { visuals?: unknown }).visuals;
  const visuals: ExerciseVisualBlock[] = Array.isArray(raw) ? (raw as ExerciseVisualBlock[]) : [];

  const handleChange = (next: ExerciseVisualBlock[]) => {
    const newData = { ...block.data };
    if (next.length === 0) {
      delete (newData as Record<string, unknown>).visuals;
    } else {
      (newData as Record<string, unknown>).visuals = next;
    }
    onPatchData(newData);
  };

  return (
    <section className="mt-3 space-y-2 border-t border-slate-100 pt-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        Éléments visuels{visuals.length > 0 ? ` (${visuals.length})` : ''}
      </p>
      <VisualBlocksEditor
        blocks={visuals}
        onChange={handleChange}
        isDisabled={isDisabled}
        onIsolate={onIsolateVisual ? (v) => onIsolateVisual(v.id) : undefined}
      />
    </section>
  );
}
